import { Box, Button, Flex, Loading, Text, createColumnHelper } from 'ui'
import { useMemo } from 'react'
import { usePulseContext } from '../../context/PulseProvider'
import { useFillRateByCompany } from 'queries/pulse/useFillRateByCompany'
import { FillRateByCompany as FillRateByCompanyType } from 'api/pulse/fillRateByCompany'
import { formatNumber, formatPercentage } from 'lib/string'
import { InfiniteTable } from '../InfiniteTable'
import { useFillRateByCompanyDownloadCsv } from 'queries/pulse/useFillRateByCompanyDownloadCsv'
import { formatTimePeriodDate } from 'pages/PulsePage/utils'

const columnHelper = createColumnHelper<FillRateByCompanyType[0]>()

export function FillRateByCompany() {
  const {
    state: { filters },
  } = usePulseContext()

  const filtersWithPagination = useMemo(() => {
    return {
      ...filters,
      page: 0,
      pageSize: 50,
    }
  }, [filters])

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useFillRateByCompany(filtersWithPagination)

  const { mutateAsync: downloadCsv, isPending: isDownloadingCsv } =
    useFillRateByCompanyDownloadCsv(filters)

  const flatData = useMemo(() => {
    return data?.pages.flatMap((page) => page) ?? []
  }, [data])

  const columns = [
    columnHelper.accessor('shift_date', {
      header: 'Shift Date',
      cell: (info) => (
        <Text>
          {formatTimePeriodDate(filters.timePeriod, info.getValue() as string)}
        </Text>
      ),
    }),
    columnHelper.accessor('company_name', {
      header: 'Location Name',
    }),
    columnHelper.accessor('workers_needed_final', {
      header: '# Requests',
      cell: (info) => (
        <Text>
          {info.getValue() ? formatNumber(info.getValue() as number) : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('workers_needed_final_forecast', {
      header: '# Upcoming',
      cell: (info) => (
        <Text>
          {info.getValue() ? formatNumber(info.getValue() as number) : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor('fill_rate', {
      header: 'Fill Rate',
      cell: (info) => (
        <Text>
          {info.getValue()
            ? formatPercentage(info.getValue() as number)
            : 'N/A'}
        </Text>
      ),
    }),
  ]

  return (
    <Box>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        mb={3}
      >
        <Box>
          <Text fontSize={3} fontWeight="500">
            Fill Rate by Location
          </Text>
          <Text fontSize={2} color="lightText">
            Number of shifts requested paired with fill rate for the time range
            selected grouped by location
          </Text>
        </Box>
        <Button
          variant="secondary"
          onClick={() => downloadCsv()}
          loading={isDownloadingCsv}
        >
          CSV
        </Button>
      </Flex>
      {isLoading ? (
        <Box p={3}>
          <Loading />
        </Box>
      ) : (
        <InfiniteTable
          data={flatData}
          columns={columns}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          height="400px"
        />
      )}
    </Box>
  )
}
